import React from "react";
import { View, TouchableOpacity, Linking } from "react-native";
import { AppText } from "@/components/ui/AppText";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { useI18n } from "@/context/I18nContext";
import type { TranslationKeys } from "@chinese-vendor/i18n";
import { cn } from "@/lib/utils";
import { FilePreview } from "@/components/ui/FilePreview";

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function filenameFromUrl(url: string): string {
  return url.split("/").pop() ?? "document";
}

// ─── ReadOnlyField ────────────────────────────────────────────────────────────

export type FieldStatus = "rejected" | "update_required" | "updated";

/** Callback signature for the "Make Update" action on a flagged field. */
export type MakeUpdateFn = (fieldKey: string) => void;

export interface ReadOnlyFieldProps {
  labelKey: TranslationKeys;
  value?: string | null;
  /** When true the field occupies half the row on desktop (flex-1). */
  flex?: boolean;
  /** Field status — "rejected", "update_required", or "updated" — shows the appropriate badge. */
  fieldStatus?: FieldStatus;
  /** Called when the vendor taps "Make Update" on an update_required field. */
  onMakeUpdate?: () => void;
}

export function ReadOnlyField({
  labelKey,
  value,
  flex,
  fieldStatus,
  onMakeUpdate,
}: ReadOnlyFieldProps) {
  const { t } = useI18n();
  return (
    <View className={cn("items-start w-full pt-2", flex && "flex-1")}>
      <AppText weight="medium" className="text-base text-supporting-text">
        {t(labelKey)}
      </AppText>
      <View className="py-4">
        {value != null ? (
          <AppText weight="medium" className="text-lg text-dark-text-50">
            {value}
          </AppText>
        ) : (
          <AppText weight="medium" className="text-lg text-dark-text-10">
            —
          </AppText>
        )}
      </View>
      {fieldStatus && (
        <View className="flex-row items-center gap-4 pb-2">
          {fieldStatus === "updated" ? (
            <Badge
              shape="rounded"
              variant="accent-blue"
              label="vendor.application.detail.fieldUpdated"
            />
          ) : (
            <>
              <Badge
                shape="rounded"
                variant={fieldStatus === "update_required" ? "filled-warning" : "filled-error"}
                label={
                  fieldStatus === "update_required"
                    ? "vendor.application.detail.fieldUpdateRequired"
                    : "vendor.application.detail.fieldRejected"
                }
              />
              {fieldStatus === "update_required" && (
                <Button
                  variant="primary"
                  label="vendor.application.detail.makeUpdate"
                  className="px-2 lg:px-4 py-1 lg:py-2"
                  onPress={onMakeUpdate ?? (() => {})}
                />
              )}
            </>
          )}
        </View>
      )}
    </View>
  );
}

// ─── FileCard ─────────────────────────────────────────────────────────────────

export interface FileCardProps {
  url: string;
  /** Field status — "rejected", "update_required", or "updated" — shows the appropriate badge. */
  fieldStatus?: FieldStatus;
  /** Called when the vendor taps "Make Update" on an update_required file field. */
  onMakeUpdate?: () => void;
}

export function FileCard({ url, fieldStatus, onMakeUpdate }: FileCardProps) {
  return (
    <View className="items-start w-full">
      <FilePreview className="border-0 p-0" fileKey={url} />
      {fieldStatus && (
        <View className="flex-row items-center gap-2 pb-2">
          {fieldStatus === "updated" ? (
            <Badge variant="accent-blue" label="vendor.application.detail.fieldUpdated" />
          ) : (
            <>
              <Badge
                variant={fieldStatus === "update_required" ? "filled-warning" : "filled-error"}
                label={
                  fieldStatus === "update_required"
                    ? "vendor.application.detail.fieldUpdateRequired"
                    : "vendor.application.detail.fieldRejected"
                }
              />
              {fieldStatus === "update_required" && (
                <Button
                  variant="primary"
                  label="vendor.application.detail.makeUpdate"
                  onPress={onMakeUpdate ?? (() => {})}
                />
              )}
            </>
          )}
        </View>
      )}
    </View>
  );
}
